import {
  clearTokens,
  getAccessToken,
  hasStoredSession,
  refreshSession,
} from './hubAuth';

// API Base URL configuration
// In production, the API is served from the same domain
// In development, it connects to localhost or specified URL
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

// Thrown by fetchAPI when the backend signals "you need to sign in via
// the hub" (401 + X-Hub-Login-URL). The fetcher kicks off
// `window.location.href = ...` immediately, but the browser doesn't
// actually unload until the next microtask, so React keeps rendering
// in the meantime. AuthContext catches this error specifically and
// holds a "Signing in…" overlay until navigation completes — without
// it, the SPA briefly renders the local-mode Login page during the
// transition and looks broken.
export class HubRedirectError extends Error {
  constructor(direction = 'in') {
    super(`Redirecting to hub ${direction === 'out' ? 'logout' : 'login'}`);
    this.name = 'HubRedirectError';
    this.direction = direction;
  }
}

// Thrown when the caller needs to sign in and the deployment is in
// in-app login mode (401 + X-Hub-Login-Mode: in-app). Nothing navigates:
// AuthContext catches this and renders the login form on this origin,
// which is the whole point — an installed PWA that bounces to
// hub.estreamly.co leaves its own window and, on iOS, lands in a browser
// context that can't hand the cookie back.
export class LoginRequiredError extends Error {
  constructor(reason) {
    super('Sign-in required');
    this.name = 'LoginRequiredError';
    this.reason = reason || null;
  }
}

// Thrown when the backend returns a 403 with the X-Tenant-Suspended
// header — the user authenticated successfully but their org has been
// deactivated by the super admin. Re-logging in won't help, so this is
// kept distinct from HubRedirectError; AuthContext renders a dedicated
// "account suspended" screen instead of bouncing back to the hub.
export class TenantSuspendedError extends Error {
  constructor(orgId, detail) {
    super(detail || 'Your organization account has been suspended.');
    this.name = 'TenantSuspendedError';
    this.orgId = orgId;
  }
}

// Get auth token from localStorage (local-mode Bearer only; hub mode
// authenticates via the .estreamly.co `estreamly_token` cookie).
function getAuthToken() {
  const user = localStorage.getItem('bb_auth_user');
  if (user) {
    try {
      const parsed = JSON.parse(user);
      return parsed.token || null;
    } catch (e) {
      return null;
    }
  }
  return null;
}

function bearerIfReal() {
  const token = getAuthToken();
  if (!token) return null;
  // Frontend-only fallback tokens aren't real JWTs; sending them as
  // Bearer would shadow the hub cookie and get rejected by the backend.
  if (token.startsWith('frontend-only-')) return null;
  return token;
}

// The credential to present, in preference order:
//   1. A hub access token from the in-app sign-in (renewed if stale).
//   2. A local-mode Bearer token (USE_HUB_AUTH=false deployments).
//   3. Nothing — the browser still sends the `estreamly_token` cookie
//      on its own for desktop SSO sessions.
export async function authorizationHeader() {
  const hubToken = await getAccessToken();
  if (hubToken) return { Authorization: `Bearer ${hubToken}` };
  const local = bearerIfReal();
  if (local) return { Authorization: `Bearer ${local}` };
  return {};
}

// Decide what a 401 means and act on it.
//
// In-app mode never navigates: it raises LoginRequiredError and lets the
// SPA render its own form. Redirect mode keeps the historical full-page
// bounce to the hub, so the flow can be reverted from the environment
// (HUB_TOKEN_AUTH=false) without shipping a new bundle.
function handleUnauthorized(response) {
  const mode = response.headers.get('X-Hub-Login-Mode');
  if (mode === 'in-app') {
    clearTokens();
    throw new LoginRequiredError('unauthorized');
  }
  const loginUrl = response.headers.get('X-Hub-Login-URL');
  if (loginUrl) {
    window.location.href = rewriteNextToCurrentPage(loginUrl);
    throw new HubRedirectError('in');
  }
}

// The backend's X-Hub-Login-URL header sets ?next= to whatever URL it
// happened to receive — for an XHR that's an /api/... endpoint. Rewrite
// it to the SPA URL the user is actually on so the post-login bounce
// lands on the React app, not on a raw JSON response.
function rewriteNextToCurrentPage(loginUrl) {
  try {
    const u = new URL(loginUrl, window.location.origin);
    u.searchParams.set('next', window.location.href);
    return u.toString();
  } catch (e) {
    return loginUrl;
  }
}

async function fetchAPI(endpoint, options = {}, { isRetry = false } = {}) {
  const url = `${API_BASE_URL}${endpoint}`;

  const headers = {
    'Content-Type': 'application/json',
    ...(await authorizationHeader()),
    ...options.headers,
  };

  const response = await fetch(url, {
    credentials: 'include',   // send the .estreamly.co hub SSO cookie
    ...options,
    headers,
  });

  if (response.status === 401) {
    // An access token can expire mid-session (they're deliberately
    // short). If we hold a refresh token, renew once and replay the
    // request before concluding the user is signed out. refreshSession
    // is single-flight, so a burst of concurrent 401s costs one rotation
    // rather than one each — which matters, because parallel rotations
    // look like token replay to the hub and would revoke the session.
    if (!isRetry && hasStoredSession()) {
      try {
        await refreshSession();
        return fetchAPI(endpoint, options, { isRetry: true });
      } catch (e) {
        /* refresh failed — fall through and treat this as signed out */
      }
    }
    handleUnauthorized(response);
  }

  if (response.status === 403) {
    const suspendedOrg = response.headers.get('X-Tenant-Suspended');
    if (suspendedOrg) {
      const body = await response.json().catch(() => ({}));
      const detail = typeof body.detail === 'string' ? body.detail : null;
      throw new TenantSuspendedError(suspendedOrg, detail);
    }
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Unknown error' }));
    // FastAPI sometimes returns `detail` as a structured object (e.g.
    // {message, blockers}) rather than a string — extract the message
    // text but stash the full payload on the Error so callers that
    // want to render the structured info can read it.
    let text = error.detail || error.message || 'API request failed';
    let detail = null;
    if (text && typeof text === 'object') {
      detail = text;
      text = text.message || text.detail || JSON.stringify(text);
    }
    const err = new Error(text);
    err.status = response.status;
    if (detail) err.detail = detail;
    throw err;
  }

  return response.json();
}

export const api = {
  getIdentityAudit: () => fetchAPI('/api/admin/identity-audit'),
  controlIdentityAudit: (action) => fetchAPI('/api/admin/identity-audit', {
    method: 'POST', body: JSON.stringify({ action }),
  }),
  getIdentityAuditResults: (status = 'all', offset = 0) =>
    fetchAPI(`/api/admin/identity-audit/results?status=${encodeURIComponent(status)}&offset=${offset}`),
  getResearchChanges: (status = 'pending', offset = 0) =>
    fetchAPI(`/api/admin/research-changes?status=${encodeURIComponent(status)}&offset=${offset}`),
  decideResearchChange: (id, action, note = '', values = undefined) =>
    fetchAPI(`/api/admin/research-changes/${id}/decision`, {
      method: 'POST', body: JSON.stringify({ action, note, values }),
    }),

  // Chat (/api/chat) — list/create/inspect threads + tool catalog.
  // The streaming POST that actually exchanges messages lives in
  // components/chat/chatStream.js because it needs ReadableStream
  // access, not fetchAPI's JSON shape.
  listChatThreads: () => fetchAPI('/api/chat/threads'),
  createChatThread: (title) =>
    fetchAPI('/api/chat/threads', {
      method: 'POST',
      body: JSON.stringify({ title: title || null }),
    }),
  getChatThread: (id) => fetchAPI(`/api/chat/threads/${id}`),
  deleteChatThread: (id) =>
    fetchAPI(`/api/chat/threads/${id}`, { method: 'DELETE' }),
  listChatTools: () => fetchAPI('/api/chat/tools'),

  // Auth
  login: (username, password) =>
    fetchAPI('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    }),

  getMe: () =>
    fetchAPI('/api/auth/me'),

  // In hub mode the backend deletes the estreamly_token cookie and
  // returns { logout_url: '<hub>/logout' } so the caller can clear the
  // parent-domain hub session as well.
  logout: () =>
    fetch(`${API_BASE_URL}/api/auth/logout`, {
      method: 'POST',
      credentials: 'include',
    }).then(r => r.ok ? r.json().catch(() => ({})) : ({})),

  refreshToken: () =>
    fetchAPI('/api/auth/refresh', { method: 'POST' }),

  // Tenant / org switching. Sets a server-side HttpOnly cookie that
  // overrides the JWT-derived primary org for subsequent API calls.
  switchOrg: (orgId) =>
    fetchAPI('/api/auth/switch-org', {
      method: 'POST',
      body: JSON.stringify({ org_id: orgId }),
    }),

  clearOrgOverride: () =>
    fetchAPI('/api/auth/clear-org', { method: 'POST' }),

  // Super-admin: tenant management
  listOrganizations: () =>
    fetchAPI('/api/admin/organizations'),

  updateOrganization: (orgId, patch) =>
    fetchAPI(`/api/admin/organizations/${encodeURIComponent(orgId)}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  // Super-admin: application health dashboard
  getAdminHealth: () =>
    fetchAPI('/api/admin/health'),

  // Dashboard
  getDashboard: (platform = 'instagram', topN = 10) =>
    fetchAPI(`/api/dashboard?platform=${platform}&top_n=${topN}`),

  // Brands
  getBrands: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.skip) queryParams.append('skip', params.skip);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.category) queryParams.append('category', params.category);
    if (params.search) queryParams.append('search', params.search);
    if (params.country) queryParams.append('country', params.country);
    if (params.parent_company) queryParams.append('parent_company', params.parent_company);
    if (params.marketplace) queryParams.append('marketplace', params.marketplace);
    if (params.founded_year_min != null && params.founded_year_min !== '')
      queryParams.append('founded_year_min', params.founded_year_min);
    if (params.founded_year_max != null && params.founded_year_max !== '')
      queryParams.append('founded_year_max', params.founded_year_max);
    if (params.has_instagram === true || params.has_instagram === false)
      queryParams.append('has_instagram', String(params.has_instagram));
    if (params.has_tiktok === true || params.has_tiktok === false)
      queryParams.append('has_tiktok', String(params.has_tiktok));
    if (params.recently_added_days)
      queryParams.append('recently_added_days', params.recently_added_days);
    if (params.tag_id) queryParams.append('tag_id', params.tag_id);
    return fetchAPI(`/api/brands/?${queryParams.toString()}`);
  },

  searchBrands: (query) =>
    fetchAPI(`/api/brands/search?q=${encodeURIComponent(query)}`),

  getBrand: (id) =>
    fetchAPI(`/api/brands/${id}`),

  getBrandFull: (id) =>
    fetchAPI(`/api/brands/${id}/full`),

  // Detail-page edit modal — partial update, only the fields the
  // operator touched are sent. Empty strings clear the column.
  updateBrand: (id, fields) =>
    fetchAPI(`/api/brands/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(fields),
    }),

  // Hard delete from the detail page (mistakenly added). The backend
  // cascades to social_metrics + content_posts, so historical data
  // for this brand is gone — typed-name confirmation lives in the SPA.
  deleteBrand: (id) =>
    fetchAPI(`/api/brands/${id}`, { method: 'DELETE' }),

  // Pause / resume social tracking. Inactive brands are skipped by the
  // weekly sync and the force-sync; historical metrics stay visible.
  setBrandActive: (id, isActive) =>
    fetchAPI(`/api/brands/${id}/active`, {
      method: 'PATCH',
      body: JSON.stringify({ is_active: isActive }),
    }),

  getBrandMetrics: (id, params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.platform) queryParams.append('platform', params.platform);
    if (params.days) queryParams.append('days', params.days);
    return fetchAPI(`/api/brands/${id}/metrics?${queryParams.toString()}`);
  },

  getBrandGrowth: (id, platform = 'instagram', days = 30) =>
    fetchAPI(`/api/brands/${id}/growth?platform=${platform}&days=${days}`),

  // Consumer product-sentiment: latest score + per-run snapshot history.
  getBrandSentiment: (id) =>
    fetchAPI(`/api/brands/${id}/sentiment`),

  // On-demand product-sentiment analysis (billed per run). force=true
  // re-runs even inside the freshness window.
  analyzeBrandSentiment: (id, force = false) =>
    fetchAPI(`/api/brands/${id}/sentiment/analyze${force ? '?force=true' : ''}`, {
      method: 'POST',
    }),

  // Rankings
  getRankings: (params = {}) => {
    const queryParams = new URLSearchParams();
    queryParams.append('period', params.period || '7d');
    queryParams.append('platform', params.platform || 'instagram');
    if (params.skip) queryParams.append('skip', params.skip);
    if (params.limit) queryParams.append('limit', params.limit);
    // One `category` / `country` per selected value — the endpoint ORs
    // them, so the Brands filter can hold several of each at once.
    const asList = (value) => (Array.isArray(value) ? value : [value]).filter(Boolean);
    asList(params.category).forEach((c) => queryParams.append('category', c));
    asList(params.country).forEach((c) => queryParams.append('country', c));
    if (params.region) queryParams.append('region', params.region);
    if (params.hq_state) queryParams.append('hq_state', params.hq_state);
    // A territory id, or 'unassigned'. The backend expands it to that
    // territory's HQ countries and intersects with any country filter.
    if (params.territory) queryParams.append('territory', params.territory);
    if (params.founded_year_min) queryParams.append('founded_year_min', params.founded_year_min);
    if (params.min_followers) queryParams.append('min_followers', params.min_followers);
    if (params.tag_id) queryParams.append('tag_id', params.tag_id);
    if (params.sort_by) queryParams.append('sort_by', params.sort_by);
    if (params.include_unranked) queryParams.append('include_unranked', 'true');
    return fetchAPI(`/api/rankings/?${queryParams.toString()}`);
  },

  getRankingFilters: () => fetchAPI('/api/rankings/filters'),

  getTopBrands: (period = '7d', platform = 'instagram', limit = 10) =>
    fetchAPI(`/api/rankings/top?period=${period}&platform=${platform}&limit=${limit}`),

  getFocusQueue: (limit = 10) =>
    fetchAPI(`/api/rankings/focus-queue?limit=${limit}`),

  // Movers — brands running ahead of their own four-week baseline,
  // grouped by region, with the weekly follower series behind each
  // card's sparkline and the brand's tags for the card's chip row.
  // `groupBy` cuts the columns: 'region' (default) or 'territory', the
  // latter one column per territory plus Unassigned, each carrying its
  // owner so the cards can be filtered down to "only mine".
  getMovers: (platform = 'instagram', limit = 9, groupBy = 'region') =>
    fetchAPI(
      `/api/rankings/movers?platform=${platform}&limit=${limit}&group_by=${groupBy}`,
    ),

  // Focus Queue scoring config — the tenant's named custom fit score.
  // GET returns { config|null, variables (grouped catalog), options
  // (categories/countries for the parameter pickers), weight_min/max }.
  getFocusScoreConfig: () =>
    fetchAPI('/api/focus-config/'),

  // Upsert: { score_name, enabled, weights: {key: -10..10}, params }.
  saveFocusScoreConfig: (payload) =>
    fetchAPI('/api/focus-config/', {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),

  // Remove the custom score — Focus Queue reverts to momentum.
  deleteFocusScoreConfig: () =>
    fetchAPI('/api/focus-config/', { method: 'DELETE' }),

  // Rank brands with an unsaved draft recipe (config page live preview).
  previewFocusScore: (payload, limit = 10) =>
    fetchAPI(`/api/focus-config/preview?limit=${limit}`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  // Metrics
  getMetrics: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.brand_id) queryParams.append('brand_id', params.brand_id);
    if (params.platform) queryParams.append('platform', params.platform);
    if (params.days) queryParams.append('days', params.days);
    return fetchAPI(`/api/metrics/?${queryParams.toString()}`);
  },

  getMetricsSummary: (days = 30) =>
    fetchAPI(`/api/metrics/summary?days=${days}`),

  // Categories
  getCategories: () =>
    fetchAPI('/api/categories'),

  // Brand tags — org-scoped labels pinned on brands, rendered as chips
  // on the Brands grid, brand detail, Rankings and dashboard analytics.
  // listTags returns { tags: [{id, name, color, brand_count}], count }.
  listTags: () =>
    fetchAPI('/api/tags/'),

  createTag: (name, color = null) =>
    fetchAPI('/api/tags/', {
      method: 'POST',
      body: JSON.stringify({ name, color }),
    }),

  updateTag: (tagId, fields) =>
    fetchAPI(`/api/tags/${tagId}`, {
      method: 'PATCH',
      body: JSON.stringify(fields),
    }),

  // Deletes the tag org-wide (it disappears from every brand).
  deleteTag: (tagId) =>
    fetchAPI(`/api/tags/${tagId}`, { method: 'DELETE' }),

  // Territories — HQ countries owned by a colleague. listTerritories
  // returns { territories: [{id, name, owner_email, owner_name,
  // countries, color, brand_count}], unassigned: {brand_count,
  // countries}, count }.
  listTerritories: () =>
    fetchAPI('/api/territories/'),

  // Colleagues who can own one: this org's known users, as suggestions
  // (the picker also accepts a typed address).
  listTerritoryMembers: () =>
    fetchAPI('/api/territories/members'),

  createTerritory: (payload) =>
    fetchAPI('/api/territories/', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  updateTerritory: (territoryId, fields) =>
    fetchAPI(`/api/territories/${territoryId}`, {
      method: 'PATCH',
      body: JSON.stringify(fields),
    }),

  // The countries fall back to Unassigned; no brand is touched.
  deleteTerritory: (territoryId) =>
    fetchAPI(`/api/territories/${territoryId}`, { method: 'DELETE' }),

  // Pin a tag on a brand: pass { tag_id } for an existing tag or
  // { name } to create-or-reuse by name. Returns { tag, brand }.
  addBrandTag: (brandId, payload) =>
    fetchAPI(`/api/brands/${brandId}/tags`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  // Unpin from one brand; the tag stays in the org catalog.
  removeBrandTag: (brandId, tagId) =>
    fetchAPI(`/api/brands/${brandId}/tags/${tagId}`, { method: 'DELETE' }),

  // Export
  exportBrands: (format = 'csv') => {
    const bearer = bearerIfReal();
    return fetch(`${API_BASE_URL}/api/export/brands?format=${format}`, {
      credentials: 'include',
      headers: bearer ? { 'Authorization': `Bearer ${bearer}` } : {},
    });
  },

  // CRM leads in the SAP ByD "New Marketing Lead" template layout,
  // covering the followed brands shown on the Following tab. Format is
  // whichever the user picked in the export menu: 'csv' or 'xlsx'.
  exportCrmLeads: (format = 'csv') => {
    const bearer = bearerIfReal();
    return fetch(`${API_BASE_URL}/api/export/crm-leads?format=${format}`, {
      credentials: 'include',
      headers: bearer ? { 'Authorization': `Bearer ${bearer}` } : {},
    });
  },

  exportMetrics: (brandId, format = 'csv') => {
    const url = brandId
      ? `${API_BASE_URL}/api/export/metrics?brand_id=${brandId}&format=${format}`
      : `${API_BASE_URL}/api/export/metrics?format=${format}`;
    const bearer = bearerIfReal();
    return fetch(url, {
      credentials: 'include',
      headers: bearer ? { 'Authorization': `Bearer ${bearer}` } : {},
    });
  },

  // Sync
  triggerSync: () =>
    fetchAPI('/api/sync', { method: 'POST' }),

  // Credits / Usage
  getCreditsUsage: (days = 30) =>
    fetchAPI(`/api/credits/usage?days=${days}`),

  getCreditsBudget: () =>
    fetchAPI('/api/credits/budget'),

  // Brand Research
  researchWebsite: (websiteUrl) =>
    fetchAPI('/api/brands/research/website', {
      method: 'POST',
      body: JSON.stringify({ website_url: websiteUrl }),
    }),

  // Scan a product (phone flow). Multipart like uploadBrandsCsv — the
  // browser sets the boundary, so no Content-Type header. Returns
  // { identified: {brand_name, product_name, ...}, existing_brand | null,
  // message }. Nothing is written server-side.
  identifyBrandPhoto: async (file) => {
    const form = new FormData();
    form.append('image', file, file.name || 'product.jpg');
    const resp = await fetch(`${API_BASE_URL}/api/brands/research/photo`, {
      method: 'POST',
      credentials: 'include',
      headers: await authorizationHeader(),
      body: form,
    });
    if (resp.status === 401) {
      handleUnauthorized(resp);
    }
    const body = await resp.json().catch(() => ({}));
    if (!resp.ok) {
      const detail = body.detail;
      const text = detail && typeof detail === 'object'
        ? (detail.message || JSON.stringify(detail))
        : (detail || body.message || 'Could not identify the photo');
      const err = new Error(text);
      err.status = resp.status;
      throw err;
    }
    return body;
  },

  // Land a brand in the review queue by hand (the photo sheet's
  // confirm step). Returns { created, pending | null, existing_brand |
  // null } — `created: false` means the name matched a tracked brand.
  createPendingBrand: (fields) =>
    fetchAPI('/api/brands/research/pending', {
      method: 'POST',
      body: JSON.stringify(fields),
    }),

  // Super-admin only — re-run the deep-research pipeline on one brand
  // to fill any structured fields the original ingestion left blank.
  rerunDeepResearch: (brandId) =>
    fetchAPI(`/api/brands/${brandId}/research/deep`, { method: 'POST' }),

  // Contact tools (MoltSets) — open to all users, org-scoped and
  // credit-gated. Two phases so the expensive enrichment is only spent
  // on contacts the operator actually picks.

  // Phase 1 — cheap roster + keyword-bucketed suggestions. Returns
  // { success, domain, founder, roster: [...], suggestions: { rnd, procurement,
  // leadership }, notes }. Each roster person carries a `roles` array.
  // Pass { domain } or { company } to override the company searched when
  // the brand's stored website points at the wrong org.
  scoutContacts: (brandId, override = {}) =>
    fetchAPI(`/api/brands/${brandId}/contacts/scout`, {
      method: 'POST',
      body: JSON.stringify({ domain: override.domain, company: override.company }),
    }),

  // Find a candidate's business email (cascade: LinkedIn -> email, then
  // name + domain -> email). A verified email is billed and auto-saved
  // onto a brand contact so it survives reloads; the response then also
  // carries { contact, brand }. An unverified guess is free and NOT
  // persisted. Returns { email, verified, guess, message, ... }.
  findContactEmail: (brandId, candidate) =>
    fetchAPI(`/api/brands/${brandId}/contacts/find-email`, {
      method: 'POST',
      body: JSON.stringify({
        linkedin_url: candidate.linkedin_url,
        full_name: candidate.full_name,
        title: candidate.title,
        role: candidate.role,
      }),
    }),

  // Save a scouted/manual person as a brand contact with a role tag
  // (founder | procurement | rnd | leadership | other). Reuses the inline
  // business email for free; only a missing email costs a lookup.
  // Returns { success, contact, brand }.
  saveContact: (brandId, candidate) =>
    fetchAPI(`/api/brands/${brandId}/contacts/save`, {
      method: 'POST',
      body: JSON.stringify(candidate),
    }),

  // Share a brand's intelligence report by email (brand card Share
  // button). Sends the profile, links, latest metrics and saved
  // contacts to a colleague via SendGrid. The optional recipient name
  // is stored on their follower row so the Following tab can show a
  // person instead of an address. Returns { success, message }.
  shareBrand: (brandId, recipientEmail, message = '', recipientName = '') =>
    fetchAPI(`/api/brands/${brandId}/share`, {
      method: 'POST',
      body: JSON.stringify({
        recipient_email: recipientEmail,
        recipient_name: recipientName || null,
        message: message || null,
      }),
    }),

  // Following page — every brand at least one team member follows
  // (report recipients), with who follows it and since when. Each brand
  // also carries hq_city/country/region, current_followers and the
  // movers-style growth_pct / baseline_multiple; each follower carries
  // is_user (has an account here → "colleague"). Returns { brands,
  // count, follower_count, watched_count }.
  getFollowedBrands: () =>
    fetchAPI('/api/brands/followed'),

  // Un-follow: remove one team member from a brand's follower list
  // (Following card chips / "Stop following"). Returns { success, brand }.
  removeBrandFollower: (brandId, followerId) =>
    fetchAPI(`/api/brands/${brandId}/followers/${followerId}`, {
      method: 'DELETE',
    }),

  // Fetch a saved contact's mobile number (the pricey step).
  // Returns { success, phone, contact, brand, message }.
  fetchContactPhone: (brandId, contactId) =>
    fetchAPI(`/api/brands/${brandId}/contacts/${contactId}/phone`, {
      method: 'POST',
    }),

  // Remove a saved contact. Returns { success, brand }.
  deleteContact: (brandId, contactId) =>
    fetchAPI(`/api/brands/${brandId}/contacts/${contactId}`, {
      method: 'DELETE',
    }),

  // Products (lineup + per-SKU ingredient lists). Manual writes mark
  // the row source="manual" so research never overwrites them.
  // All return { success, product?, brand }.
  saveProduct: (brandId, product) =>
    fetchAPI(`/api/brands/${brandId}/products`, {
      method: 'POST',
      body: JSON.stringify(product),
    }),

  // On-demand standalone product-lineup research (billed per run).
  // force=true re-runs inside the freshness window and purges stale
  // research-sourced SKUs (manual rows are never touched).
  // Returns { success, message, skipped?, changes?, brand }.
  researchBrandProducts: (brandId, force = false) =>
    fetchAPI(`/api/brands/${brandId}/products/research${force ? '?force=true' : ''}`, {
      method: 'POST',
    }),

  // Per-run product-research history: { success, snapshots }.
  getProductSnapshots: (brandId) =>
    fetchAPI(`/api/brands/${brandId}/products/snapshots`),

  updateProduct: (brandId, productId, product) =>
    fetchAPI(`/api/brands/${brandId}/products/${productId}`, {
      method: 'PUT',
      body: JSON.stringify(product),
    }),

  deleteProduct: (brandId, productId) =>
    fetchAPI(`/api/brands/${brandId}/products/${productId}`, {
      method: 'DELETE',
    }),

  // Operator comments on a brand — human annotations no research run
  // ever touches. Delete is author-or-super-admin only (backend gate).
  // Both return { success, comment?, brand }.
  addComment: (brandId, body) =>
    fetchAPI(`/api/brands/${brandId}/comments`, {
      method: 'POST',
      body: JSON.stringify({ body }),
    }),

  deleteComment: (brandId, commentId) =>
    fetchAPI(`/api/brands/${brandId}/comments/${commentId}`, {
      method: 'DELETE',
    }),

  // Super-admin only — bulk panel: list incomplete brands, kick off a
  // background re-run, poll for progress.
  listIncompleteBrands: () =>
    fetchAPI('/api/brands/research/deep/incomplete'),

  // mode='fill' (default) only writes blank columns; mode='overwrite'
  // targets every brand and replaces researched fields. Used by the
  // super-admin "Force re-research all brands" panel when past runs
  // produced poor data.
  startBulkDeepResearch: (mode = 'fill') =>
    fetchAPI(
      `/api/brands/research/deep/bulk?mode=${encodeURIComponent(mode)}`,
      { method: 'POST' },
    ),

  getBulkDeepResearchJob: (jobId) =>
    fetchAPI(`/api/brands/research/deep/bulk/${jobId}`),

  // Super-admin only — force-run the weekly social sync immediately
  // for the currently-viewed org. Returns { eligible_brands, active_job }
  // for the preview, { job_id } when started, and the full job row for
  // polling (single-flight per org enforced server-side).
  previewForceSocialSync: () =>
    fetchAPI('/api/brands/social-sync/force/preview'),

  startForceSocialSync: () =>
    fetchAPI('/api/brands/social-sync/force', { method: 'POST' }),

  getForceSocialSyncJob: (jobId) =>
    fetchAPI(`/api/brands/social-sync/force/${jobId}`),

  getPendingBrands: (status) => {
    const queryParams = new URLSearchParams();
    if (status) queryParams.append('status', status);
    return fetchAPI(`/api/brands/research/pending?${queryParams.toString()}`);
  },

  approvePendingBrand: (pendingId) =>
    fetchAPI(`/api/brands/research/pending/${pendingId}/approve`, { method: 'POST' }),

  rejectPendingBrand: (pendingId) =>
    fetchAPI(`/api/brands/research/pending/${pendingId}/reject`, { method: 'POST' }),

  restorePendingBrand: (pendingId) =>
    fetchAPI(`/api/brands/research/pending/${pendingId}/restore`, { method: 'POST' }),

  updatePendingBrand: (pendingId, fields) =>
    fetchAPI(`/api/brands/research/pending/${pendingId}`, {
      method: 'PATCH',
      body: JSON.stringify(fields),
    }),

  getUpdateLogs: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.brand_id) queryParams.append('brand_id', params.brand_id);
    if (params.days) queryParams.append('days', params.days);
    return fetchAPI(`/api/brands/research/updates?${queryParams.toString()}`);
  },

  // Bulk CSV upload — async. Returns { job_id, status, total_rows };
  // poll getCsvUploadJob(job_id) every couple seconds until status is
  // 'completed' or 'failed'.
  uploadBrandsCsv: async (file) => {
    const form = new FormData();
    form.append('file', file);
    const resp = await fetch(`${API_BASE_URL}/api/brands/upload-csv`, {
      method: 'POST',
      credentials: 'include',
      headers: await authorizationHeader(),             // NB: no Content-Type — browser sets the multipart boundary
      body: form,
    });
    if (resp.status === 401) {
      handleUnauthorized(resp);
    }
    const body = await resp.json().catch(() => ({}));
    if (!resp.ok) {
      throw new Error(body.detail || body.message || 'Upload failed');
    }
    return body;
  },

  getCsvUploadJob: (jobId) =>
    fetchAPI(`/api/brands/upload-csv/${jobId}`),

  csvUploadTemplateUrl: () =>
    `${API_BASE_URL}/api/brands/upload-csv/template`,

  // Tradeshow (desktop only) — upload an exhibitor spreadsheet, map its
  // columns onto brand attributes, research every row, download the
  // augmented table. Separate from the pipeline: nothing lands in the
  // review queue until addTradeshowRowToPipeline is called for a row.

  // Multipart like uploadBrandsCsv. Parks the file as a draft list and
  // returns { list_id, name, columns, sample_rows, total_rows,
  // suggested_mapping, fields, warnings } for the mapping dialog.
  uploadTradeshowSpreadsheet: async (file) => {
    const form = new FormData();
    form.append('file', file, file.name || 'list.csv');
    const resp = await fetch(`${API_BASE_URL}/api/tradeshow/upload`, {
      method: 'POST',
      credentials: 'include',
      headers: await authorizationHeader(),
      body: form,
    });
    if (resp.status === 401) {
      handleUnauthorized(resp);
    }
    const body = await resp.json().catch(() => ({}));
    if (!resp.ok) {
      const detail = body.detail;
      const text = detail && typeof detail === 'object'
        ? (detail.message || JSON.stringify(detail))
        : (detail || body.message || 'Upload failed');
      const err = new Error(text);
      err.status = resp.status;
      throw err;
    }
    return body;
  },

  // Confirm the mapping ({ header: attribute | null }) and the list's
  // name; research starts in the background. Returns { list }.
  startTradeshowList: (listId, name, mapping) =>
    fetchAPI(`/api/tradeshow/lists/${listId}/start`, {
      method: 'POST',
      body: JSON.stringify({ name, mapping }),
    }),

  // Saved lists, newest first: { lists, count }.
  listTradeshowLists: () =>
    fetchAPI('/api/tradeshow/lists'),

  // One list with its rows: { list, rows }. Polled while running.
  getTradeshowList: (listId) =>
    fetchAPI(`/api/tradeshow/lists/${listId}`),

  renameTradeshowList: (listId, name) =>
    fetchAPI(`/api/tradeshow/lists/${listId}`, {
      method: 'PATCH',
      body: JSON.stringify({ name }),
    }),

  // Also cancels a draft that never got a mapping.
  deleteTradeshowList: (listId) =>
    fetchAPI(`/api/tradeshow/lists/${listId}`, { method: 'DELETE' }),

  // The per-row button. 400 when research marked the row as not a
  // beverage company. Returns { row, created, brand_id, pending_id,
  // message }.
  addTradeshowRowToPipeline: (listId, rowId) =>
    fetchAPI(`/api/tradeshow/lists/${listId}/rows/${rowId}/pipeline`, {
      method: 'POST',
    }),

  // Download the augmented table ('csv' | 'xlsx') — the original
  // columns first, then the researched ones. Resolves to the Response.
  exportTradeshowList: async (listId, format = 'csv') =>
    fetch(`${API_BASE_URL}/api/tradeshow/lists/${listId}/export?format=${format}`, {
      credentials: 'include',
      headers: await authorizationHeader(),
    }),
};

